/**
 * TrustpilotBadge Component
 *
 * A simple, static Trustpilot badge that doesn't require API access or paid plan.
 * Shows rating, stars, and links to the Trustpilot review page.
 *
 * Use this as a fallback when:
 * - You don't have a paid Trustpilot business plan
 * - The official widget is blocked by tracking protection
 * - You want a lightweight, always-working badge
 *
 * For the official Trustpilot TrustBox widget, use TrustpilotWidget.tsx instead.
 *
 * Accessibility:
 * - Uses semantic HTML and proper link text
 * - Stars have aria-label for screen readers
 * - Proper color contrast
 */

import { Star } from 'lucide-react';

type RatingText = 'Excellent' | 'Great' | 'Average' | 'Poor' | 'Bad';
type BadgeSize = 'sm' | 'md' | 'lg';

interface TrustpilotBadgeProps {
    /** Rating from 1-5 (supports decimals like 4.7) */
    rating?: number;
    /** Text description of rating */
    ratingText?: RatingText;
    /** Total number of reviews (optional) */
    reviewCount?: number;
    /** Your domain on Trustpilot */
    domain?: string;
    /** Size variant */
    size?: BadgeSize;
    /** Show "See our reviews" text */
    showReviewLink?: boolean;
    /** Additional CSS classes */
    className?: string;
    /** Layout direction */
    layout?: 'horizontal' | 'vertical';
    /** Theme: light (default) or dark (for dark backgrounds) */
    theme?: 'light' | 'dark';
}

// Trustpilot brand colors
const TRUSTPILOT_GREEN = '#00b67a';
const TRUSTPILOT_STAR_COLOR = '#00b67a';
const TRUSTPILOT_STAR_EMPTY = '#dcdce6';

// Size configurations
const sizeConfig: Record<BadgeSize, { starSize: number; textSize: string; logoHeight: string }> = {
    sm: { starSize: 14, textSize: 'text-xs', logoHeight: 'h-4' },
    md: { starSize: 18, textSize: 'text-sm', logoHeight: 'h-5' },
    lg: { starSize: 22, textSize: 'text-base', logoHeight: 'h-6' },
};

/**
 * Renders Trustpilot-style star rating
 */
const StarRating = ({ rating, size }: { rating: number; size: BadgeSize }) => {
    const { starSize } = sizeConfig[size];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.25 && rating % 1 < 0.75;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0) - (rating % 1 >= 0.75 ? 1 : 0);
    const almostFullStar = rating % 1 >= 0.75;

    return (
        <div
            className="flex items-center gap-0.5"
            role="img"
            aria-label={`Rated ${rating} out of 5 stars`}
        >
            {/* Full stars */}
            {Array.from({ length: fullStars }).map((_, i) => (
                <div
                    key={`full-${i}`}
                    className="p-0.5"
                    style={{ backgroundColor: TRUSTPILOT_STAR_COLOR }}
                >
                    <Star
                        size={starSize}
                        fill="white"
                        stroke="white"
                        strokeWidth={0}
                    />
                </div>
            ))}

            {/* Almost full star (>=0.75) */}
            {almostFullStar && (
                <div
                    key="almost-full"
                    className="p-0.5"
                    style={{ backgroundColor: TRUSTPILOT_STAR_COLOR }}
                >
                    <Star
                        size={starSize}
                        fill="white"
                        stroke="white"
                        strokeWidth={0}
                    />
                </div>
            )}

            {/* Half star */}
            {hasHalfStar && (
                <div
                    key="half"
                    className="p-0.5 relative overflow-hidden"
                    style={{ backgroundColor: TRUSTPILOT_STAR_EMPTY }}
                >
                    <Star
                        size={starSize}
                        fill="white"
                        stroke="white"
                        strokeWidth={0}
                    />
                    <div
                        className="absolute inset-0 overflow-hidden"
                        style={{ width: '50%', backgroundColor: TRUSTPILOT_STAR_COLOR }}
                    >
                        <div className="p-0.5">
                            <Star
                                size={starSize}
                                fill="white"
                                stroke="white"
                                strokeWidth={0}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Empty stars */}
            {Array.from({ length: emptyStars }).map((_, i) => (
                <div
                    key={`empty-${i}`}
                    className="p-0.5"
                    style={{ backgroundColor: TRUSTPILOT_STAR_EMPTY }}
                >
                    <Star
                        size={starSize}
                        fill="white"
                        stroke="white"
                        strokeWidth={0}
                    />
                </div>
            ))}
        </div>
    );
};

/**
 * Trustpilot logo (text version)
 */
const TrustpilotLogo = ({ size, theme = 'light' }: { size: BadgeSize; theme?: 'light' | 'dark' }) => {
    const { logoHeight, textSize } = sizeConfig[size];
    const textColor = theme === 'dark' ? '#ffffff' : '#191919';

    return (
        <div className={`flex items-center gap-1 ${logoHeight}`}>
            <svg
                viewBox="0 0 126 31"
                xmlns="http://www.w3.org/2000/svg"
                className={logoHeight}
                aria-label="Trustpilot"
            >
                <path
                    d="M33.3 0L35.8 7.5H44.1L37.5 12.2L40 19.7L33.3 14.9L26.7 19.7L29.2 12.2L22.6 7.5H30.9L33.3 0Z"
                    fill={TRUSTPILOT_GREEN}
                />
                <path
                    d="M40.5 14.3L40 12.8L33.3 17.5L40.5 14.3Z"
                    fill="#005128"
                />
            </svg>
            <span className={`font-semibold ${textSize}`} style={{ color: textColor }}>
                Trustpilot
            </span>
        </div>
    );
};

export const TrustpilotBadge = ({
    rating = 4.8,
    ratingText = 'Excellent',
    reviewCount,
    domain,
    size = 'md',
    showReviewLink = true,
    className = '',
    layout = 'horizontal',
    theme = 'light',
}: TrustpilotBadgeProps) => {
    const { textSize } = sizeConfig[size];

    // Get domain from props or environment
    const trustpilotDomain = domain || import.meta.env.VITE_TRUSTPILOT_DOMAIN || 'trustpilot.com';
    const trustpilotUrl = `https://www.trustpilot.com/review/${trustpilotDomain}`;

    const isVertical = layout === 'vertical';
    const isDark = theme === 'dark';

    // Theme-based colors
    const ratingTextColor = isDark ? '#ffffff' : '#191919';
    const subtextColor = isDark ? 'text-white/70' : 'text-muted-foreground';
    const hoverBg = isDark ? 'hover:bg-white/10' : 'hover:bg-muted';
    const focusRingOffset = isDark ? 'focus:ring-offset-slate-900' : 'focus:ring-offset-2';

    return (
        <a
            href={trustpilotUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`
                inline-flex ${isVertical ? 'flex-col items-center' : 'flex-row items-center'}
                gap-2 p-2 rounded-lg
                ${hoverBg} transition-colors
                focus:outline-none focus:ring-2 focus:ring-ring ${focusRingOffset}
                ${className}
            `}
            aria-label={`Rated ${ratingText} on Trustpilot - ${rating} out of 5 stars${reviewCount ? ` based on ${reviewCount} reviews` : ''}. Click to see reviews.`}
        >
            {/* Rating text and stars */}
            <div className={`flex ${isVertical ? 'flex-col items-center' : 'flex-row items-center'} gap-1`}>
                <span
                    className={`font-semibold ${textSize}`}
                    style={{ color: ratingTextColor }}
                >
                    {ratingText}
                </span>
                <StarRating rating={rating} size={size} />
            </div>

            {/* Review count and Trustpilot logo */}
            <div className={`flex ${isVertical ? 'flex-col items-center' : 'flex-row items-center'} gap-0.5`}>
                {reviewCount !== undefined && (
                    <span className={`${textSize} ${subtextColor}`}>
                        {reviewCount.toLocaleString()} reviews on
                    </span>
                )}
                {reviewCount === undefined && showReviewLink && (
                    <span className={`${textSize} ${subtextColor}`}>
                        See our reviews on
                    </span>
                )}
                <TrustpilotLogo size={size} theme={theme} />
            </div>
        </a>
    );
};

export default TrustpilotBadge;
